// Van Design — client-side single source of truth.
// Module catalogue, budget tiers, clearance validation, and blueprint derivation
// (DIY + Pro) all derive from ONE module model so every screen stays in sync.

export const BUDGET_TIERS = {
  low: {
    key: "low", label: "Low-End", mult: 1.0,
    board: "15mm structural plywood", hardware: "basic hinges + fixed shelves",
    finish: "raw / clear-oiled", blurb: "Standard board, basic hardware, simple finish.",
    projectRange: [4000, 8000],
  },
  mid: {
    key: "mid", label: "Mid-Range", mult: 1.6,
    board: "18mm birch plywood", hardware: "soft-close hinges + drawer runners",
    finish: "laminate / painted", blurb: "Upgraded materials, soft-close hardware, tidy finish.",
    projectRange: [9000, 16000],
  },
  high: {
    key: "high", label: "High-End", mult: 2.6,
    board: "18mm marine / veneer plywood", hardware: "premium soft-close + push-latch",
    finish: "veneer / 2-pac spray", blurb: "Premium materials, premium hardware, custom detailing.",
    projectRange: [18000, 35000],
  },
};

// footprint w (across width) x h (along length) in mm; heightMM = build height.
export const MODULE_CATALOG = {
  bed_platform:      { label: "Bed Platform",       w: 1400, h: 1900, heightMM: 950,  base: 650,  diff: 2, cat: "sleep",   anchor: true,  standing: false },
  seating_bench:     { label: "Seating Bench",      w: 1100, h: 500,  heightMM: 450,  base: 480,  diff: 2, cat: "seat",    anchor: true,  standing: false },
  kitchenette:       { label: "Galley Kitchen",     w: 1000, h: 600,  heightMM: 900,  base: 900,  diff: 4, cat: "kitchen", anchor: false, standing: false },
  drawer_cabinet:    { label: "Drawer Cabinet",     w: 600,  h: 550,  heightMM: 900,  base: 520,  diff: 3, cat: "storage", anchor: false, standing: false },
  overhead_storage:  { label: "Overhead Storage",   w: 1600, h: 350,  heightMM: 350,  base: 380,  diff: 2, cat: "storage", anchor: false, standing: false },
  shelving:          { label: "Open Shelving",      w: 900,  h: 300,  heightMM: 700,  base: 220,  diff: 1, cat: "storage", anchor: false, standing: false },
  utility_box:       { label: "Gear / Garage Box",  w: 700,  h: 500,  heightMM: 500,  base: 300,  diff: 1, cat: "storage", anchor: false, standing: false },
  fridge:            { label: "Fridge",             w: 550,  h: 550,  heightMM: 550,  base: 1200, diff: 2, cat: "kitchen", anchor: false, standing: false },
  desk:              { label: "Work Desk",          w: 700,  h: 450,  heightMM: 740,  base: 300,  diff: 2, cat: "work",    anchor: false, standing: false },
  water_system:      { label: "Water System",       w: 600,  h: 400,  heightMM: 500,  base: 600,  diff: 3, cat: "utility", anchor: false, standing: false },
  electrical_cabinet:{ label: "Electrical Cabinet", w: 500,  h: 450,  heightMM: 450,  base: 950,  diff: 4, cat: "utility", anchor: false, standing: false },
  shower_pod:        { label: "Shower Pod",          w: 800,  h: 800,  heightMM: 1900, base: 1400, diff: 5, cat: "bath",    anchor: false, standing: true  },
  toilet:            { label: "Toilet",             w: 500,  h: 500,  heightMM: 450,  base: 350,  diff: 1, cat: "bath",    anchor: false, standing: false },
};

export const MODULE_TYPES = Object.keys(MODULE_CATALOG);

export const GOAL_PRIORITIES = [
  "bed", "storage", "kitchenette", "cabinetry", "work desk", "gear storage", "seating", "toilet", "shower",
];

const WALKWAY_MIN = 400; // mm

export function tierOf(key) { return BUDGET_TIERS[key] || BUDGET_TIERS.mid; }
export function catalogOf(type) { return MODULE_CATALOG[type] || null; }

export function newModule(type, interior) {
  const c = catalogOf(type);
  if (!c) return null;
  const w = Math.min(c.w, interior.width - 80);
  const h = Math.min(c.h, interior.length - 80);
  return { id: Math.random().toString(36).slice(2, 10), type, x: 40, y: 40, w, h, note: "" };
}

export function moduleCost(mod, tierKey) {
  const c = catalogOf(mod.type);
  if (!c) return 0;
  return Math.round(c.base * tierOf(tierKey).mult);
}

export function projectEstimate(modules, tierKey) {
  return (modules || []).reduce((s, m) => s + moduleCost(m, tierKey), 0);
}

export function diffLabel(n) { return ["", "Very Easy", "Easy", "Moderate", "Advanced", "Expert"][n] || "Moderate"; }

// ------------------------------ validation --------------------------------
function overlap(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

// Returns { perModule: {id: [warnings]}, global: [warnings], corridor: mm }
export function validateLayout(van, modules) {
  const interior = van?.interior || { length: 3200, width: 1750, height: 1850, arch_width: 1400, arch_y: [1500, 2050] };
  const perModule = {};
  const globalW = [];
  const list = modules || [];
  list.forEach((m) => { perModule[m.id] = []; });

  // bounds + wheel arch + door zones + standing
  const archX0 = (interior.width - (interior.arch_width || interior.width * 0.8)) / 2;
  const archX1 = interior.width - archX0;
  const archY = interior.arch_y || [interior.length * 0.45, interior.length * 0.63];
  const sideDoor = van?.doors?.side; // {side:'right', y:[..]}
  const rearDoor = van?.doors?.rear; // {y:[..]}

  list.forEach((m) => {
    const c = catalogOf(m.type);
    if (m.x < 0 || m.y < 0 || m.x + m.w > interior.width + 1 || m.y + m.h > interior.length + 1) {
      perModule[m.id].push("Exceeds usable interior span");
    }
    // wheel arch (floor-mounted modules only; bed sits above the arch)
    if (c && !["bed_platform", "overhead_storage"].includes(m.type)) {
      const hitL = m.x < archX0 + 10 && m.y < archY[1] && m.y + m.h > archY[0];
      const hitR = m.x + m.w > archX1 - 10 && m.y < archY[1] && m.y + m.h > archY[0];
      if (hitL || hitR) perModule[m.id].push("Wheel-arch conflict");
    }
    // standing clearance
    if (c && c.standing && interior.height < 1850) {
      perModule[m.id].push("Standing clearance issue (low roof)");
    }
    // side door
    if (sideDoor) {
      const onSide = sideDoor.side === "right" ? (m.x + m.w > interior.width - 260) : (m.x < 260);
      if (onSide && m.y < sideDoor.y[1] && m.y + m.h > sideDoor.y[0] && m.type !== "bed_platform") {
        perModule[m.id].push("Blocks the side door");
      }
    }
    // rear door
    if (rearDoor && m.y + m.h > rearDoor.y[0] - 20 && m.type !== "bed_platform" && m.type !== "overhead_storage") {
      perModule[m.id].push("Rear door conflict");
    }
  });

  // overlaps
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      if (overlap(list[i], list[j])) {
        perModule[list[i].id].push("Overlaps another module");
        perModule[list[j].id].push("Overlaps another module");
      }
    }
  }

  // widest continuous walkway (scan x columns free across full length)
  const step = 50;
  let best = 0, run = 0;
  for (let x = 0; x <= interior.width; x += step) {
    const blocked = list.some((m) => x >= m.x && x <= m.x + m.w
      && m.y < interior.length - 100 && m.y + m.h > 100
      && m.type !== "overhead_storage");
    if (!blocked) { run += step; best = Math.max(best, run); } else { run = 0; }
  }
  if (best < WALKWAY_MIN) globalW.push(`Walkway too tight (${best} mm, need ${WALKWAY_MIN} mm)`);

  return { perModule, global: globalW, corridor: best };
}

export function layoutWarningCount(van, modules) {
  const v = validateLayout(van, modules);
  const pm = Object.values(v.perModule).reduce((s, a) => s + a.length, 0);
  return pm + v.global.length;
}

// --------------------------- build sequence -------------------------------
const SEQ_RANK = {
  electrical_cabinet: 0, water_system: 1, bed_platform: 2, seating_bench: 2,
  kitchenette: 3, fridge: 4, drawer_cabinet: 5, utility_box: 5, desk: 6,
  shower_pod: 6, toilet: 7, shelving: 8, overhead_storage: 9,
};
export function buildSequence(modules) {
  return [...(modules || [])].sort((a, b) => (SEQ_RANK[a.type] ?? 5) - (SEQ_RANK[b.type] ?? 5));
}

// --------------------------- blueprint derivation -------------------------
const HARDWARE = {
  bed_platform: ["8x slats bearers", "16x 65mm structural screws", "4x L-brackets"],
  seating_bench: ["2x soft-close hinges", "12x 50mm screws", "2x gas struts (seat lid)"],
  kitchenette: ["2x drawer runners", "4x soft-close hinges", "1x worktop sealant", "16x screws"],
  drawer_cabinet: ["3x drawer runner pairs", "3x handles", "12x screws"],
  overhead_storage: ["4x soft-close hinges", "4x cabinet brackets", "12x 40mm screws"],
  shelving: ["8x shelf pins", "4x L-brackets", "8x screws"],
  utility_box: ["1x hasp latch", "8x screws", "2x hinges"],
  fridge: ["4x mounting brackets", "1x vent kit"],
  desk: ["2x folding brackets", "8x screws"],
  water_system: ["1x pump", "2x hose clamps", "1x tank strap"],
  electrical_cabinet: ["1x DIN rail", "cable glands", "1x busbar", "fuses"],
  shower_pod: ["1x tray", "wall panels", "sealant", "1x drain kit"],
  toilet: ["2x floor brackets", "4x screws"],
};

const TOOLS = {
  1: ["Drill/driver", "Tape measure"],
  2: ["Drill/driver", "Tape measure", "Jigsaw"],
  3: ["Drill/driver", "Tape measure", "Jigsaw", "Countersink bit"],
  4: ["Drill/driver", "Circular saw", "Jigsaw", "Clamps", "Router"],
  5: ["Drill/driver", "Circular saw", "Router", "Clamps", "Sealant gun", "Multi-tool"],
};

// carcass cut list from footprint + build height (Pro)
export function proSpec(mod, van, tierKey) {
  const c = catalogOf(mod.type);
  const tier = tierOf(tierKey);
  const th = tierKey === "low" ? 15 : 18;
  const H = c.heightMM;
  const cut = [];
  if (!["overhead_storage", "shelving"].includes(mod.type)) {
    cut.push({ part: `${c.label} — Side panel`, qty: 2, size: `${mod.h} × ${H}` });
    cut.push({ part: `${c.label} — Base`, qty: 1, size: `${mod.w} × ${mod.h}` });
    cut.push({ part: `${c.label} — Top`, qty: 1, size: `${mod.w} × ${mod.h}` });
    cut.push({ part: `${c.label} — Back`, qty: 1, size: `${mod.w} × ${H}` });
  } else {
    cut.push({ part: `${c.label} — Shelf`, qty: mod.type === "shelving" ? 3 : 1, size: `${mod.w} × ${mod.h}` });
    cut.push({ part: `${c.label} — Side`, qty: 2, size: `${mod.h} × ${H}` });
    cut.push({ part: `${c.label} — Back`, qty: 1, size: `${mod.w} × ${H}` });
  }
  return {
    label: c.label,
    footprint: `${mod.w} × ${mod.h} mm  ·  H ${H} mm`,
    material: tier.board, thickness: `${th} mm`, finish: tier.finish,
    hardware: HARDWARE[mod.type] || ["screws"],
    joinery: tierKey === "high" ? "Domino / pocket-hole, glued + screwed" : (tierKey === "mid" ? "Pocket-hole, glued + screwed" : "Butt joint, screwed"),
    tolerance: tierKey === "high" ? "±1 mm" : (tierKey === "mid" ? "±2 mm" : "±3 mm"),
    install: `Mount to ${["bed_platform", "seating_bench"].includes(mod.type) ? "floor + wall studs" : "wall furring + floor cleat"}; seal edges.`,
    cut,
    cost: moduleCost(mod, tierKey),
  };
}

// progressive DIY steps: each step shows prior parts installed + current highlighted
export function diySteps(modules, van, tierKey) {
  const seq = buildSequence(modules);
  return seq.map((m, i) => {
    const c = catalogOf(m.type);
    return {
      n: i + 1,
      moduleId: m.id,
      title: `Install the ${c.label}`,
      installedIds: seq.slice(0, i).map((x) => x.id),   // prior parts remain visible
      currentId: m.id,
      parts: [`${c.label} flat-pack panels (pre-cut, labelled ${c.label.split(" ")[0].toUpperCase()}-${i + 1})`],
      hardware: HARDWARE[m.type] || ["screws"],
      tools: TOOLS[c.diff] || TOOLS[2],
      action: `Position the ${c.label.toLowerCase()} ${m.y < (van?.interior?.length || 3200) / 2 ? "toward the front" : "toward the rear"}, level it, then fix to the wall furring and floor cleats.`,
      safety: c.diff >= 4 ? "Wear eye protection; isolate 12V power before wiring." : "Pre-drill to avoid splitting; keep fixings clear of chassis lines.",
      diff: c.diff,
    };
  });
}
